import { useState, useCallback, useMemo } from "react";
import { fetchWithCustomPagination } from "@/utils/helper/graphql-helper";
import { listQuickReorders } from "@/graphql/queries";
import { generateClient } from "@/utils/helper/aws-generateClient-singleton";
import { QuickReorder } from "@/types/AppTypes";

const client = generateClient();

export const useQuickReorders = ({
  limit = 10,
  infoVariablesP,
  onLoadingChange,
  abortControllerRef,
}: {
  limit?: number;
  infoVariablesP?: any;
  onLoadingChange?: (loading: boolean) => void;
  abortControllerRef?: React.RefObject<AbortController | null>;
}) => {
  const [quickReorders, setQuickReorders] = useState<QuickReorder[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [previousTokens, setPreviousTokens] = useState<(string | null)[]>([]);

  const infoVariables = useMemo(() => {
    return (
      infoVariablesP || {
        filter: {},
      }
    );
  }, [infoVariablesP]);

  const loadPage = useCallback(
    async (token: string | null | undefined = null, signal?: AbortSignal) => {
      if (onLoadingChange) {
        onLoadingChange(true);
      }
      setLoading(true);

      try {
        const result = await fetchWithCustomPagination({
          query: listQuickReorders,
          variables: {
            limit,
            nextToken: token,
            ...infoVariables,
          },
          client,
          showToast: false,
          signal,
        });

        setQuickReorders(result?.items);
        setNextToken(result?.nextToken);
        setPreviousTokens((prev) => [...prev, token]);

        return result?.items;
      } catch (error: any) {
        console.error("Error loading quick reorders:", error);
        return [];
      } finally {
        if (onLoadingChange) {
          onLoadingChange(false);
        }
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [limit, infoVariables]
  );

  const loadNextPage = useCallback(() => {
    if (nextToken) {
      return loadPage(nextToken);
    }
    return Promise.resolve([]);
  }, [nextToken, loadPage]);

  const loadPreviousPage = useCallback(() => {
    if (previousTokens.length > 1) {
      const previousToken = previousTokens[previousTokens.length - 2];
      setPreviousTokens((prev) => prev.slice(0, -1));
      return loadPage(previousToken);
    }
    return Promise.resolve([]);
  }, [previousTokens, loadPage]);

  return {
    quickReorders,
    loading,
    hasNextPage: !!nextToken,
    nextToken,
    loadNextPage,
    loadPreviousPage,
    loadPage,
    setLoading,
  };
};
